import { useEffect, useState } from "react";
import { QrCode, Loader2, Save, XCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/** Issuer-level "Scan & Pay via UPI" (one block below the bank cards). Stores the EXACT approved decoded QR text; management only. */
export function IssuerUpiQrPanel() {
  const [d, setD] = useState(null);
  const [uri, setUri] = useState("");
  const [payee, setPayee] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    api.get("/bank-accounts/issuer-upi-qr")
      .then(({ data }) => { if (live) { setD(data); setUri(data.uri || ""); setPayee(data.payee || ""); } })
      .catch((e) => live && setErr(e?.response?.status === 401 ? "Session expired — sign in again to view the UPI QR." : (e?.response?.data?.detail || "Could not load the UPI QR settings.")));
    return () => { live = false; };
  }, []);
  const shell = (msg, tid) => (
    <div className="border border-slate-200 rounded-xl p-4 text-[12px] text-slate-500 flex items-center gap-2" data-testid={tid}>
      <QrCode className="h-4 w-4 text-slate-400" /> <span className="font-semibold text-slate-700">Scan &amp; Pay via UPI</span> — {msg}
    </div>);
  if (err) return shell(err, "issuer-upi-qr-error");
  if (!d) return shell(<Loader2 className="h-3.5 w-3.5 animate-spin" />, "issuer-upi-qr-loading");
  if (d.state === "NO_ISSUER") return shell("select a company to view its UPI QR.", "issuer-upi-qr-group");
  const run = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); setD(data); setUri(data.uri || ""); setPayee(data.payee || ""); toast.success(ok); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const active = d.state === "ACTIVE";
  return (
    <div className="border border-slate-200 rounded-xl p-4" data-testid="issuer-upi-qr-panel">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <QrCode className="h-4 w-4 text-slate-500" />
        <div className="text-[13px] font-semibold text-slate-700">Scan &amp; Pay via UPI — {d.issuer}</div>
        <span className={`text-[11px] px-1.5 py-0.5 rounded ${active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid="issuer-upi-qr-state">
          {active ? `Active in reminders · v${d.version}` : "Not set up — reminders show bank details only"}</span>
      </div>
      <div className="text-[11px] text-slate-500 mb-2">One QR shown below the bank cards in payment reminders. Paste the exact text decoded from the approved QR — it is used as-is (no amount or reference added).</div>
      {active && (
        <div className="flex items-start gap-3 mb-2" data-testid="issuer-upi-qr-current">
          <img src={d.data_url} alt="Issuer UPI QR" className="h-28 w-28 border border-slate-200 bg-white" />
          <div className="text-[12px] min-w-0">
            <div><span className="text-slate-500">UPI ID </span><span className="font-mono font-semibold" data-testid="issuer-upi-qr-vpa">{d.vpa}</span></div>
            <div><span className="text-slate-500">Payee </span><span className="font-semibold" data-testid="issuer-upi-qr-payee">{d.payee}</span></div>
            <div className="font-mono break-all text-slate-700" data-testid="issuer-upi-qr-uri">{d.uri}</div>
            <div className="text-[11px] text-slate-400">Approved by {d.approved_by} · {String(d.approved_at || "").slice(0, 16).replace("T", " ")}{d.note ? ` · ${d.note}` : ""}</div>
          </div>
        </div>)}
      {d.can_edit && (
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={uri} onChange={(e) => setUri(e.target.value)} placeholder="upi://pay?pa=name@bank&pn=Name" className="h-8 flex-1 min-w-[240px] font-mono text-[12px]" data-testid="issuer-upi-qr-uri-input" />
          <Input value={payee} onChange={(e) => setPayee(e.target.value)} placeholder="Payee shown in email" className="h-8 w-44 text-[12px]" data-testid="issuer-upi-qr-payee-input" />
          <Button size="sm" disabled={busy || !uri || !payee || (uri === d.uri && payee === d.payee)} className="bg-emerald-700 hover:bg-emerald-800" data-testid="issuer-upi-qr-save"
            onClick={() => window.confirm(`Confirm this is ${d.issuer}'s approved UPI QR?\n${uri}`) && run(() => api.put("/bank-accounts/issuer-upi-qr", { uri, payee }), "Issuer UPI QR saved")}>
            <Save className="h-3.5 w-3.5 mr-1" /> Save</Button>
          {active && <Button size="sm" variant="outline" disabled={busy} data-testid="issuer-upi-qr-clear"
            onClick={() => window.confirm("Remove the UPI QR from reminders?") && run(() => api.post("/bank-accounts/issuer-upi-qr/clear"), "Issuer UPI QR removed")}>
            <XCircle className="h-3.5 w-3.5 mr-1" /> Remove</Button>}
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
        </div>)}
    </div>
  );
}
